import { SearchAccountCardTotalsError } from 'core/cuenta/models/errors/SearchAccountCardTotalsError';
import { useCoreService } from 'core/services/useCoreService';
import Moment from 'moment';
import { HOME_PATH } from 'navigation/routes';
import { createContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConsolidatedReport } from '../models/ConsolidatedReport';
import { SEARCH_BUSINESS_COSTCENTRE_REPORT_PATH } from '../routes';
import { useDataFetcher } from 'shared/http';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { useNotifications } from 'shared/notifications';

interface IBusinessTotalsReportContext {
  consolidatedReport: ConsolidatedReport;
  goHome(): void;
}

export const BusinessTotalsReportContext = createContext<IBusinessTotalsReportContext>({
  consolidatedReport: null,
  goHome: () => {},
});

export const BusinessTotalsReportProvider = ({ children }) => {
  const coreService = useCoreService();
  const [consolidatedReport, setConsolidatedReportRequest] =
    useDataFetcher<ConsolidatedReport>(null);
  const { addNotification } = useNotifications();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const processResponse = (response) => {
      let list = response.liquidacionCuentas
        ? response.liquidacionCuentas
        : response.liquidacionCentroCostos;
      if (
        response.liquidacionCuentas?.length < 12 ||
        response.liquidacionCentroCostos?.length < 12
      ) {
        const currentDate = Moment().startOf('month');
        for (let i = 0; i < 12; i++) {
          currentDate.subtract(1, 'month');
          if (list[i] == null || Moment(list[i].periodo).isAfter(currentDate)) {
            list.push({ periodo: currentDate.format('YYYY-MM-DD'), saldoPesos: 0.0 });
          }
          list = list.sort((a, b) => Moment(b.periodo).diff(Moment(a.periodo)));
        }
      }
      return {
        centroCostoId: response.centroCostoId,
        denominacionEmpresa: response.denominacionEmpresa,
        liquidacion: list,
      };
    };
    const catchError = (err) => {
      const message = new SearchAccountCardTotalsError(err.error).message;
      addNotification(message, 'error');
      navigate(HOME_PATH);
      return null;
    };
    if (SEARCH_BUSINESS_COSTCENTRE_REPORT_PATH.match(pathname)) {
      setConsolidatedReportRequest(() =>
        coreService.getCostCentreLast12Months().then(processResponse).catch(catchError),
      );
    } else {
      setConsolidatedReportRequest(() =>
        coreService.getAccountLast12Months().then(processResponse).catch(catchError),
      );
    }
  }, [pathname, coreService, addNotification, navigate, setConsolidatedReportRequest]);

  const contextValue = {
    consolidatedReport: consolidatedReport.data,
    goHome: () => {
      navigate(HOME_PATH);
    },
  };
  return (
    <BusinessTotalsReportContext.Provider value={contextValue}>
      <LoadingSpinner isLoading={consolidatedReport.isLoading}>{children}</LoadingSpinner>
    </BusinessTotalsReportContext.Provider>
  );
};
